import axios from "axios";
import { getToken } from "../services/storageService";
import { resolveGatewayBaseUrl } from "../runtime/loadSharedDashboardConfig";

const normalizeHttpBase = (raw: string) =>
  raw.startsWith("http") ? raw : `https://${raw}`;

const getAuthBaseUrl = () => {
  const base = resolveGatewayBaseUrl();
  return base ? normalizeHttpBase(base) : "";
};

const getStagingBaseUrl = () => {
  const base = resolveGatewayBaseUrl();
  return base ? normalizeHttpBase(base) : "";
};

export const authApiClient = axios.create({
  baseURL: getAuthBaseUrl(),
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const stagingApiClient = axios.create({
  baseURL: getStagingBaseUrl(),
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const attachBearerToken = (config: any) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

authApiClient.interceptors.request.use(attachBearerToken);
stagingApiClient.interceptors.request.use(attachBearerToken);
export const authPostRequest = async <T = any>(endpoint: string, payload?: any): Promise<T> => {
  const res = await authApiClient.post<T>(endpoint, payload);
  return res.data;
};

export const authGetRequest = async <T = any>(
  endpoint: string,
  params?: any,
  config?: { signal?: AbortSignal; headers?: Record<string, string>; bearer?: string },
): Promise<T> => {
  const authHeaders: Record<string, string> = { ...(config?.headers ?? {}) };
  const explicitBearer = config?.bearer?.trim();
  if (explicitBearer) {
    authHeaders.Authorization = `Bearer ${explicitBearer}`;
  }
  const res = await authApiClient.get<T>(endpoint, {
    params,
    signal: config?.signal,
    headers: authHeaders,
  });
  return res.data;
};

/**
 * Unwraps gateway session profile payloads: either { data: user } or nested { data: { data: user } }.
 * Keeps localStorage and sidebar/navbar working when the backend nests the profile.
 */
export function unwrapAuthMeUser(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  const inner = root.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const layer = inner as Record<string, unknown>;
    const nested = layer.data;
    const n = nested && typeof nested === "object" && !Array.isArray(nested) ? (nested as Record<string, unknown>) : null;
    if (n && (n.id != null || n.email != null || n.full_name != null)) {
      return n;
    }
    if (layer.id != null || layer.email != null || layer.full_name != null) {
      return layer;
    }
  }
  if (root.id != null || root.email != null || root.full_name != null) {
    return root;
  }
  return null;
}

export interface AuthClientContext {
  authUser: Record<string, any>;
  roles: string[];
  isViewer: boolean;
  userId: string;
  creatorId: string;
  effectiveClientId: string;
}

export function getAuthClientContext(): AuthClientContext {
  let authUser: Record<string, any> = {};
  try {
    authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
  } catch {
    authUser = {};
  }

  const roles = Array.isArray(authUser?.roles)
    ? authUser.roles.map((r: unknown) => String(r))
    : [];
  const isViewer = roles.some((r) => r.toUpperCase() === "VIEWER");
  const userId = String(
    authUser?.id ??
      localStorage.getItem("auth_user_id") ??
      localStorage.getItem("clientId") ??
      ""
  ).trim();
  const creatorId = String(authUser?.creator_id ?? authUser?.creatorId ?? "").trim();
  const effectiveClientId = isViewer && creatorId ? creatorId : userId;

  return { authUser, roles, isViewer, userId, creatorId, effectiveClientId };
}

export function getEffectiveClientId(): string {
  return getAuthClientContext().effectiveClientId;
}

/** GET request to gateway (REACT_APP_API_GATEWAY_URL), e.g. /location/search */
export const stagingGetRequest = async <T = any>(endpoint: string, params?: any): Promise<T> => {
  const res = await stagingApiClient.get<T>(endpoint, { params });
  return res.data;
};

export const authPutRequest = async <T = any>(
  endpoint: string,
  payload?: any,
  params?: any
): Promise<T> => {
  const res = await authApiClient.put<T>(endpoint, payload, { params });
  return res.data;
};

export const authDeleteRequest = async <T = any>(endpoint: string, params?: any): Promise<T> => {
  const res = await authApiClient.delete<T>(endpoint, { params });
  return res.data;
};
